<template>
  <div 
    class="file-upload"
    @drop.prevent="handleDrop"
    @dragover.prevent
    @dragenter.prevent
  >
    <Card 
      :class="cn(
        'relative border-2 border-dashed transition-colors',
        isDragging && 'border-primary bg-primary/5',
        'hover:border-primary/50 cursor-pointer'
      )"
      @click="triggerFileInput"
    >
      <CardContent class="flex flex-col items-center justify-center p-6 text-center">
        <input
          ref="fileInput"
          type="file"
          :multiple="multiple"
          :accept="accept"
          class="hidden"
          @change="handleFileSelect"
        />
        
        <CloudUpload class="h-12 w-12 text-muted-foreground mb-4" />
        
        <div class="gap-y-2">
          <p class="text-sm font-medium">
            <slot name="text">
              Drag files here or <span class="text-primary">click to browse</span>
            </slot>
          </p>
          <p v-if="$slots.tip" class="text-xs text-muted-foreground">
            <slot name="tip" />
          </p>
        </div>

        <div v-if="uploading" class="absolute inset-0 flex items-center justify-center bg-background/80">
          <Spinner size="lg" label="Uploading..." />
        </div>
      </CardContent>
    </Card>

    <div v-if="files.length > 0" class="mt-4 gap-y-2">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="flex items-center justify-between rounded-lg border p-3"
      >
        <div class="flex items-center gap-x-3">
          <File class="h-4 w-4 text-muted-foreground" />
          <div class="text-sm">
            <p class="font-medium">{{ file.name }}</p>
            <p class="text-xs text-muted-foreground">{{ formatFileSize(file.size) }}</p>
          </div>
        </div>
        <Button
          v-if="!autoUpload"
          size="sm"
          variant="ghost"
          @click="removeFile(index)"
        >
          <X class="h-4 w-4" />
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Spinner } from '../spinner'
import { cn } from '@/lib/utils'
import { CloudUpload, File, X } from 'lucide-vue-next'

interface Props {
  multiple?: boolean
  accept?: string
  httpRequest?: (options: UploadRequestOptions) => Promise<any>
  autoUpload?: boolean
  drag?: boolean
}

interface UploadRequestOptions {
  file: File
  onProgress?: (event: ProgressEvent) => void
  onSuccess?: (response: any) => void
  onError?: (error: Error) => void
}

const props = withDefaults(defineProps<Props>(), {
  multiple: false,
  autoUpload: true,
  drag: true
})

const emit = defineEmits<{
  'change': [files: File[]]
  'upload-success': [response: any, file: File]
  'upload-error': [error: Error, file: File]
}>()

const fileInput = ref<HTMLInputElement>()
const files = ref<File[]>([])
const isDragging = ref(false)
const uploading = ref(false)

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files) {
    handleFiles(Array.from(target.files))
  }
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  if (event.dataTransfer?.files) {
    handleFiles(Array.from(event.dataTransfer.files))
  }
}

const handleFiles = async (newFiles: File[]) => {
  if (props.multiple) {
    files.value = [...files.value, ...newFiles]
  } else {
    files.value = newFiles.slice(0, 1)
  }
  
  emit('change', files.value)
  
  if (props.autoUpload && props.httpRequest) {
    await uploadFiles(files.value)
  }
}

const uploadFiles = async (filesToUpload: File[]) => {
  if (!props.httpRequest) return
  
  uploading.value = true
  
  for (const file of filesToUpload) {
    try {
      const response = await props.httpRequest({
        file,
        onProgress: (event) => {
          // Handle progress if needed
        },
        onSuccess: (response) => {
          emit('upload-success', response, file)
        },
        onError: (error) => {
          emit('upload-error', error, file)
        }
      })
      
      emit('upload-success', response, file)
    } catch (error) {
      emit('upload-error', error as Error, file)
    }
  }
  
  uploading.value = false
  files.value = []
}

const removeFile = (index: number) => {
  files.value.splice(index, 1)
  emit('change', files.value)
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

// Public methods
const selectFile = () => {
  triggerFileInput()
}

const upload = () => {
  if (files.value.length > 0 && props.httpRequest) {
    return uploadFiles(files.value)
  }
}

defineExpose({
  selectFile,
  upload,
  click: selectFile,
  files
})
</script>